// Admin SEO-oversigt: tabel over alle ruter med deres metadata
// så jeg hurtigt kan se manglende/duplikerede titler & descriptions.
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState, useMemo } from "react";
import { toast } from "sonner";
import { getSeoAuditReport, type RouteSeoInfo } from "@/lib/seo-audit.functions";
import { refreshFacebookCache } from "@/lib/seo-refresh.functions";
import { clearSeoFixCache, listCachedSeoFilenames } from "@/lib/seo-cache.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle, CheckCircle2, Database, Download, ExternalLink, GitCommit, Image as ImageIcon, RefreshCw, Search, Sparkles, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { FbScrapeHistoryCard } from "@/components/admin/FbScrapeHistoryCard";
import { FeedPreviewDownloadCard } from "@/components/admin/FeedPreviewDownloadCard";
import { SeoFixDialog } from "@/components/admin/SeoFixDialog";
import { SeoBatchDialog } from "@/components/admin/SeoBatchDialog";
import { SeoResolvedOverviewCard } from "@/components/admin/SeoResolvedOverviewCard";
import { NoindexAuditCard } from "@/components/admin/NoindexAuditCard";
import { SitemapValidationCard } from "@/components/admin/SitemapValidationCard";
import { SeoHealthCard } from "@/components/admin/SeoHealthCard";

export const Route = createFileRoute("/admin/seo")({
  head: () => ({
    meta: [{ title: "SEO-oversigt – AutoXChange Admin" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  component: AdminSeoPage,
});

// Triggers a browser file download for an in-memory string.
function triggerDownload(filename: string, body: string, mimeType: string) {
  const blob = new Blob([body], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// Build a YYYYMMDD-HHmm timestamp suffix for filenames.
function reportTimestamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
}

// Convert a list of route audit rows into a flat CSV (RFC-4180 quoting).
function routesToCsv(routes: RouteSeoInfo[]): string {
  const headers = [
    "filename",
    "routePath",
    "isExcluded",
    "excludedReason",
    "isDynamic",
    "hasHead",
    "hasBuildSeoMeta",
    "title",
    "titleLength",
    "description",
    "descriptionLength",
    "image",
    "imageSource",
    "warnings",
    "analysisErrors",
  ];
  const escape = (v: unknown): string => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    // Quote if it contains ", comma, newline, or starts with whitespace
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const rows = routes.map((r) =>
    [
      r.filename,
      r.routePath,
      r.isExcluded,
      r.excludedReason ?? "",
      r.isDynamic,
      r.hasHead,
      r.hasBuildSeoMeta,
      r.title ?? "",
      r.title?.length ?? 0,
      r.description ?? "",
      r.description?.length ?? 0,
      r.image ?? "",
      r.imageSource,
      r.warnings.join(" | "),
      r.analysisErrors
        .map((e) => `${e.code}${e.line ? `:L${e.line}` : ""} ${e.message}`)
        .join(" | "),
    ]
      .map(escape)
      .join(","),
  );
  // BOM so Excel detects UTF-8 (ø/æ/å in danske advarsler).
  return "\uFEFF" + headers.join(",") + "\n" + rows.join("\n") + "\n";
}

function AdminSeoPage() {
  const fetchReport = useServerFn(getSeoAuditReport);
  const refreshFb = useServerFn(refreshFacebookCache);
  const clearCache = useServerFn(clearSeoFixCache);
  const fetchCachedFilenames = useServerFn(listCachedSeoFilenames);
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-seo-audit"],
    queryFn: () => fetchReport(),
  });

  const { data: cachedData } = useQuery({
    queryKey: ["admin-seo-cached-filenames"],
    queryFn: () => fetchCachedFilenames(),
  });
  const cachedFilenames = useMemo(
    () => new Set(cachedData?.filenames ?? []),
    [cachedData],
  );

  const clearCacheMutation = useMutation({
    mutationFn: () => clearCache(),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["admin-seo-cached-filenames"] });
      toast.success("AI-cache ryddet", {
        description: `${res.deleted} cachede forslag slettet. Næste AI Fix regenererer fra bunden.`,
      });
    },
    onError: (err) => {
      toast.error("Kunne ikke rydde cache", {
        description: err instanceof Error ? err.message : "Ukendt fejl",
      });
    },
  });

  const refreshMutation = useMutation({
    mutationFn: () => refreshFb(),
    onSuccess: (res) => {
      const p = res.payload;
      queryClient.invalidateQueries({ queryKey: ["fb-scrape-logs"] });
      if (p.status === "skipped") {
        toast.warning("Facebook ikke konfigureret", {
          description:
            "Tilføj FACEBOOK_APP_ID + FACEBOOK_APP_SECRET som secrets for at aktivere OG-cache refresh.",
        });
        return;
      }
      const ok = p.succeeded ?? 0;
      const failed = p.failed ?? 0;
      toast.success(`Facebook OG-cache opdateret`, {
        description: `${ok} biler scrapet${failed > 0 ? `, ${failed} fejlede` : ""} (${p.durationMs ?? 0}ms)`,
      });
    },
    onError: (err) => {
      toast.error("Kunne ikke opdatere FB-cache", {
        description: err instanceof Error ? err.message : "Ukendt fejl",
      });
    },
  });

  const [filter, setFilter] = useState<"all" | "warnings" | "errors" | "indexed" | "excluded">("warnings");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [batchOpen, setBatchOpen] = useState(false);

  const toggleSelected = (filename: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(filename)) next.delete(filename);
      else next.add(filename);
      return next;
    });
  };

  const routes = data?.routes ?? [];

  const filtered = useMemo(() => {
    return routes.filter((r) => {
      if (filter === "warnings" && r.warnings.length === 0 && !r.hasAnalysisErrors) return false;
      if (filter === "errors" && !r.hasAnalysisErrors) return false;
      if (filter === "indexed" && r.isExcluded) return false;
      if (filter === "excluded" && !r.isExcluded) return false;
      if (search) {
        const q = search.toLowerCase();
        const errText = r.analysisErrors.map((e) => `${e.code} ${e.message}`).join(" ");
        const haystack =
          `${r.routePath} ${r.title ?? ""} ${r.description ?? ""} ${errText}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [routes, filter, search]);

  const stats = useMemo(() => {
    const indexed = routes.filter((r) => !r.isExcluded);
    return {
      total: routes.length,
      indexed: indexed.length,
      withWarnings: indexed.filter((r) => r.warnings.length > 0).length,
      withErrors: routes.filter((r) => r.hasAnalysisErrors).length,
      dynamic: indexed.filter((r) => r.isDynamic).length,
      withCustomImage: indexed.filter((r) => r.imageSource === "per-route" || r.imageSource === "loader-data").length,
    };
  }, [routes]);

  /** Ruter der KAN batch-fixes: ikke-ekskluderet, ikke-dynamisk, har advarsler. */
  const batchEligibleInFiltered = useMemo(
    () => filtered.filter((r) => !r.isExcluded && !r.isDynamic && r.warnings.length > 0),
    [filtered],
  );
  const allEligibleSelected =
    batchEligibleInFiltered.length > 0 &&
    batchEligibleInFiltered.every((r) => selected.has(r.filename));
  const selectedRoutes = useMemo(
    () => routes.filter((r) => selected.has(r.filename)),
    [routes, selected],
  );

  const toggleAllVisible = () => {
    if (allEligibleSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        for (const r of batchEligibleInFiltered) next.delete(r.filename);
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        for (const r of batchEligibleInFiltered) next.add(r.filename);
        return next;
      });
    }
  };

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-6 p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold">SEO-oversigt</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Scanner alle route-filer og viser deres metadata. Brug det til at finde manglende eller duplikerede titler/descriptions.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 sm:shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!data || routes.length === 0}
                  title={
                    !data
                      ? "Vent til rapporten er hentet"
                      : "Download hele SEO-auditten som fil"
                  }
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download SEO-rapport
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  disabled={!data}
                  onSelect={() => {
                    if (!data) return;
                    const ts = reportTimestamp();
                    const payload = {
                      generatedAt: data.generatedAt,
                      exportedAt: new Date().toISOString(),
                      stats,
                      routes: data.routes,
                    };
                    triggerDownload(
                      `seo-audit-${ts}.json`,
                      JSON.stringify(payload, null, 2),
                      "application/json",
                    );
                    toast.success(`Downloadet seo-audit-${ts}.json`);
                  }}
                >
                  JSON (struktureret)
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={!data}
                  onSelect={() => {
                    if (!data) return;
                    const ts = reportTimestamp();
                    triggerDownload(
                      `seo-audit-${ts}.csv`,
                      routesToCsv(data.routes),
                      "text/csv;charset=utf-8",
                    );
                    toast.success(`Downloadet seo-audit-${ts}.csv`);
                  }}
                >
                  CSV (Excel/Sheets)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  disabled={clearCacheMutation.isPending}
                >
                  <Trash2 className={`mr-2 h-4 w-4 ${clearCacheMutation.isPending ? "animate-pulse" : ""}`} />
                  {clearCacheMutation.isPending ? "Rydder…" : "Ryd AI-cache"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Ryd alle cachede AI-forslag?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Sletter alle rækker i <code className="rounded bg-muted px-1 py-0.5 text-xs">seo_fix_cache</code>.
                    Næste gang du klikker AI Fix på en rute, regenereres forslaget fra bunden
                    (koster credits). Brug det hvis prompten er blevet ændret og alle gamle
                    forslag skal kasseres.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuller</AlertDialogCancel>
                  <AlertDialogAction onClick={() => clearCacheMutation.mutate()}>
                    Ryd cache
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button
              type="button"
              variant="outline"
              onClick={() => refreshMutation.mutate()}
              disabled={refreshMutation.isPending}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${refreshMutation.isPending ? "animate-spin" : ""}`} />
              {refreshMutation.isPending ? "Opdaterer…" : "Refresh FB-cache (50 nyeste biler)"}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 md:grid-cols-6">
          <StatCard label="Ruter i alt" value={stats.total} />
          <StatCard label="Indekserede" value={stats.indexed} />
          <StatCard
            label="Analyse-fejl"
            value={stats.withErrors}
            tone={stats.withErrors > 0 ? "warning" : "ok"}
          />
          <StatCard label="Med advarsler" value={stats.withWarnings} tone={stats.withWarnings > 0 ? "warning" : "ok"} />
          <StatCard label="Dynamiske" value={stats.dynamic} />
          <StatCard label="Med eget billede" value={stats.withCustomImage} />
        </div>

        <Card>
          <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle>Ruter</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Søg path, title…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-56 pl-8"
                />
              </div>
              <FilterButton
                active={filter === "errors"}
                onClick={() => setFilter("errors")}
                tone={stats.withErrors > 0 ? "error" : undefined}
              >
                Analyse-fejl ({stats.withErrors})
              </FilterButton>
              <FilterButton active={filter === "warnings"} onClick={() => setFilter("warnings")}>
                Advarsler ({stats.withWarnings})
              </FilterButton>
              <FilterButton active={filter === "indexed"} onClick={() => setFilter("indexed")}>
                Indekserede ({stats.indexed})
              </FilterButton>
              <FilterButton active={filter === "all"} onClick={() => setFilter("all")}>
                Alle ({stats.total})
              </FilterButton>
              <FilterButton active={filter === "excluded"} onClick={() => setFilter("excluded")}>
                Ekskluderede ({stats.total - stats.indexed})
              </FilterButton>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading && <p className="py-8 text-center text-sm text-muted-foreground">Henter rapport…</p>}
            {error && (
              <p className="py-8 text-center text-sm text-destructive">
                Fejl ved hentning: {(error as Error).message}
              </p>
            )}
            {!isLoading && !error && (
              <>
                {selected.size > 0 && (
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-primary/40 bg-primary/5 px-3 py-2">
                    <div className="text-sm">
                      <span className="font-medium">{selected.size}</span> rute
                      {selected.size === 1 ? "" : "r"} valgt til batch-fix
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelected(new Set())}
                      >
                        Fjern markering
                      </Button>
                      <Button type="button" size="sm" onClick={() => setBatchOpen(true)}>
                        <GitCommit className="mr-1.5 h-3.5 w-3.5" />
                        Batch-fix {selected.size} ruter
                      </Button>
                    </div>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[40px]">
                          <input
                            type="checkbox"
                            checked={allEligibleSelected}
                            onChange={toggleAllVisible}
                            disabled={batchEligibleInFiltered.length === 0}
                            className="h-4 w-4 cursor-pointer accent-primary disabled:opacity-30"
                            title={
                              batchEligibleInFiltered.length === 0
                                ? "Ingen ruter med advarsler i visning"
                                : allEligibleSelected
                                  ? "Fravælg alle synlige"
                                  : `Vælg alle ${batchEligibleInFiltered.length} med advarsler`
                            }
                          />
                        </TableHead>
                        <TableHead className="w-[180px]">Route</TableHead>
                        <TableHead>Title</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="w-[120px]">OG-billede</TableHead>
                        <TableHead className="w-[260px]">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                            Ingen ruter matcher filteret.
                          </TableCell>
                        </TableRow>
                      )}
                      {filtered.map((r) => (
                        <RouteRow
                          key={r.filename}
                          route={r}
                          selected={selected.has(r.filename)}
                          onToggleSelected={() => toggleSelected(r.filename)}
                          hasCached={cachedFilenames.has(r.filename)}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {batchOpen && selectedRoutes.length > 0 && (
          <SeoBatchDialog
            routes={selectedRoutes}
            open={batchOpen}
            onOpenChange={(o) => {
              setBatchOpen(o);
              if (!o) setSelected(new Set());
            }}
          />
        )}

        <SeoHealthCard />

        <SeoResolvedOverviewCard />

        <NoindexAuditCard />

        <SitemapValidationCard />

        <FeedPreviewDownloadCard />

        <FbScrapeHistoryCard />
      </div>
    </TooltipProvider>
  );
}

function StatCard({ label, value, tone }: { label: string; value: number; tone?: "ok" | "warning" }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <p
          className={`mt-1 text-2xl font-bold ${
            tone === "warning" ? "text-destructive" : tone === "ok" ? "text-primary" : ""
          }`}
        >
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

function FilterButton({
  active,
  onClick,
  tone,
  children,
}: {
  active: boolean;
  onClick: () => void;
  tone?: "error";
  children: React.ReactNode;
}) {
  const inactiveClasses =
    tone === "error"
      ? "border-destructive/40 bg-destructive/5 text-destructive hover:bg-destructive/10"
      : "border-border bg-background text-muted-foreground hover:text-foreground";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
        active
          ? tone === "error"
            ? "border-destructive bg-destructive text-destructive-foreground"
            : "border-primary bg-primary text-primary-foreground"
          : inactiveClasses
      }`}
    >
      {children}
    </button>
  );
}

function RouteRow({
  route,
  selected,
  onToggleSelected,
  hasCached,
}: {
  route: RouteSeoInfo;
  selected: boolean;
  onToggleSelected: () => void;
  hasCached: boolean;
}) {
  const titleLen = route.title?.length ?? 0;
  const descLen = route.description?.length ?? 0;
  const [fixOpen, setFixOpen] = useState(false);
  const isBatchEligible = !route.isExcluded && !route.isDynamic && route.warnings.length > 0;

  const rowBg = route.isExcluded
    ? ""
    : route.hasAnalysisErrors
      ? "bg-destructive/15"
      : route.warnings.length > 0
        ? "bg-destructive/5"
        : "";

  return (
    <>
      <TableRow className={rowBg}>
        {route.hasAnalysisErrors && !route.isExcluded && (
          <TableCell colSpan={6} className="border-l-4 border-l-destructive bg-destructive/10 py-2">
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-destructive">
                <AlertTriangle className="h-3.5 w-3.5" />
                Analyse mislykkedes — fix disse før øvrige advarsler kan stoles på:
              </div>
              {route.analysisErrors.map((e, i) => (
                <div key={`${e.code}-${i}`} className="flex items-start gap-2 pl-5 text-[11px]">
                  <Badge variant="outline" className="border-destructive/40 px-1 py-0 font-mono text-[10px] text-destructive">
                    {e.code}
                  </Badge>
                  <span className="text-foreground">{e.message}</span>
                  {e.line !== undefined && (
                    <span className="text-muted-foreground">linje {e.line}</span>
                  )}
                </div>
              ))}
            </div>
          </TableCell>
        )}
      </TableRow>
      <TableRow className={rowBg}>
        <TableCell className="align-top">
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelected}
            disabled={!isBatchEligible}
            className="h-4 w-4 cursor-pointer accent-primary disabled:opacity-30"
            title={isBatchEligible ? "Vælg til batch-fix" : "Kan ikke batch-fixes"}
          />
        </TableCell>
        <TableCell className="font-mono text-xs align-top">
          <div className="flex items-start gap-2">
            {!route.isExcluded && !route.isDynamic && (
              <a
                href={route.routePath}
                target="_blank"
                rel="noreferrer"
                className="text-muted-foreground hover:text-foreground"
                title="Åbn i ny fane"
              >
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
            <div>
              <div>{route.routePath}</div>
              {route.isDynamic && (
                <Badge variant="outline" className="mt-1 text-[10px]">
                  Dynamisk
                </Badge>
              )}
            </div>
          </div>
        </TableCell>
        <TableCell className="max-w-[280px] align-top text-sm">
          {route.title ? (
            <div>
              <p className="line-clamp-2">{route.title}</p>
              <p className={`mt-0.5 text-[10px] ${titleLen > 60 || titleLen < 20 ? "text-destructive" : "text-muted-foreground"}`}>
                {titleLen} tegn
              </p>
            </div>
          ) : route.isDynamic ? (
            <span className="text-xs italic text-muted-foreground">Genereret pr. record</span>
          ) : (
            <span className="text-xs italic text-destructive">— mangler —</span>
          )}
        </TableCell>
        <TableCell className="max-w-[360px] align-top text-sm">
          {route.description ? (
            <div>
              <p className="line-clamp-2 text-muted-foreground">{route.description}</p>
              <p className={`mt-0.5 text-[10px] ${descLen > 160 || descLen < 50 ? "text-destructive" : "text-muted-foreground"}`}>
                {descLen} tegn
              </p>
            </div>
          ) : route.isDynamic ? (
            <span className="text-xs italic text-muted-foreground">Genereret pr. record</span>
          ) : (
            <span className="text-xs italic text-destructive">— mangler —</span>
          )}
        </TableCell>
        <TableCell className="align-top">
          <ImageBadge source={route.imageSource} image={route.image} />
        </TableCell>
        <TableCell className="align-top">
          {route.isExcluded ? (
            <Badge variant="secondary" className="text-[10px]">
              {route.excludedReason}
            </Badge>
          ) : route.warnings.length === 0 ? (
            <div className="flex items-center gap-1 text-xs text-primary">
              <CheckCircle2 className="h-3.5 w-3.5" />
              OK
            </div>
          ) : (
            <div className="space-y-1">
              {route.warnings.map((w) => (
                <div key={w} className="flex items-start gap-1 text-[11px] text-destructive">
                  <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                  <span>{w}</span>
                </div>
              ))}
              {!route.isDynamic && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-1.5 h-6 gap-1 px-2 text-[10px]"
                  onClick={() => setFixOpen(true)}
                >
                  <Sparkles className="h-3 w-3" />
                  AI Fix
                  {hasCached && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="ml-0.5 inline-flex items-center text-primary" aria-label="Cached forslag findes">
                          <Database className="h-3 w-3" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">
                        Cached forslag findes — sparer AI-credits
                      </TooltipContent>
                    </Tooltip>
                  )}
                </Button>
              )}
            </div>
          )}
        </TableCell>
      </TableRow>
      {fixOpen && <SeoFixDialog route={route} open={fixOpen} onOpenChange={setFixOpen} />}
    </>
  );
}


function ImageBadge({ source, image }: { source: RouteSeoInfo["imageSource"]; image: string | null }) {
  const labels = {
    "per-route": { label: "Eget", tone: "ok" as const, tip: image ?? "" },
    "loader-data": { label: "Pr. record", tone: "ok" as const, tip: "Genereret fra loader-data" },
    default: { label: "Default", tone: "muted" as const, tip: "/og-default.jpg" },
    unknown: { label: "—", tone: "muted" as const, tip: "Ingen head()" },
  };
  const cfg = labels[source];
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          className={`gap-1 text-[10px] ${cfg.tone === "ok" ? "border-primary text-primary" : "text-muted-foreground"}`}
        >
          <ImageIcon className="h-3 w-3" />
          {cfg.label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>{cfg.tip}</TooltipContent>
    </Tooltip>
  );
}
